(function () {
    'use strict';

    angular.module('app').factory('RemarkValidator', RemarkValidatorServiceFactory);

    RemarkValidatorServiceFactory.$inject = [];
    function RemarkValidatorServiceFactory() {

        /**
         * Contains invalid SectionCodes for remarks based on the scope of the remark.
         */
        const invalidRemarkCodesByScope = {
            FVM: {
                identity: ['111'],
                activity: ['151'],
                justifications: ['123'],
                file: ['161']
            },
            relations: ['171'],
            persons: ['175'],
            justification: ['121'],
            homologations: ['131'],
            IVM: {
                Justifications: ['191'],
                Identity: ['201'],
                Nationality: ['211'],
                CivilStatus: ['221'],
                Partner: ['231'],
                Address: ['241'],
                WalkOfLife: ['251'],
                Roof: ['261'],
                Children: ['267'],
                References: ['271'],
                Attachments: ['277'],
                Signatures: ['669']
            }
        };

        /**
         * Contains valid SectionCodes for remarks based on the scope of the remark.
         */
        const okRemarkCodesByScope = {
            FVM: {
                identity: ['112'],
                activity: ['152'],
                file: ['162']
            },
            justification: ['122'],
            homologations: ['132']
        };

        return {
            OrganisationHasIndeterminateRemarks: organisationHasIndeterminateRemarks,
            OrganisationHasInvalidRemarks: organisationHasInvalidRemarks,
            OrganisationHasInvalidRemarksWithoutReason: organisationHasInvalidRemarksWithoutReason,
            OrganisationRequestSectionInvalidated: organisationRequestSectionInvalidated,
            IsOrganisationSectionChecked: isOrganisationSectionChecked,
            IndividualRequestHasInvalidRemarks: individualRequestHasInvalidRemarks,
            IndividualRequestHasInvalidRemarksWithoutReason: individualRequestHasInvalidRemarksWithoutReason
        };

        // Implementation - public

        function isOrganisationSectionChecked(request, section) {
            if (!request || !section)
                return false;

            const normalizedSection = section.toLowerCase();
            switch (normalizedSection) {
                case 'identity':
                case 'activity':
                    return containsRemarkForSection(request.Remarks, okRemarkCodesByScope.FVM[normalizedSection]);
                case 'justification':
                    const allJustifications = getAllJustifications(request);
                    return checkRemarksOnArray(allJustifications, containsRemarkForSection, okRemarkCodesByScope[normalizedSection]);
                default:
                    return true;
            }
        }

        function organisationRequestSectionInvalidated(request, section) {
            if (!request || !section)
                return false;

            const normalizedSection = section.toLowerCase();
            switch (normalizedSection) {
                case 'identity':
                case 'activity':
                    return containsRemarkForSection(request.Remarks, invalidRemarkCodesByScope.FVM[normalizedSection]) || containsIndeterminateRemark(request.Remarks);

                case 'justification':
                    const allJustifications = getAllJustifications(request);
                    return checkRemarksOnArray(allJustifications, containsRemarkForSection, invalidRemarkCodesByScope[normalizedSection])
                        || checkRemarksOnArray(allJustifications, containsIndeterminateRemark, invalidRemarkCodesByScope[normalizedSection])
                        || containsRemarkForSection(request.Remarks, invalidRemarkCodesByScope.FVM.justifications);
                case 'relations':
                    return checkRemarksOnArray(request.Persons, containsRemarkForSection, invalidRemarkCodesByScope[normalizedSection])
                        || checkRemarksOnArray(request.Persons, containsIndeterminateRemark, invalidRemarkCodesByScope[normalizedSection]);
                case 'persons':
                    return containsRemarkForSection(request.Remarks, invalidRemarkCodesByScope[normalizedSection]);

                default:
                    return false;
            }
        }

        function organisationHasIndeterminateRemarks(request) {
            if (!request)
                return false;

            return checkRemarks(request, containsIndeterminateRemark);
        }

        function organisationHasInvalidRemarks(request) {
            if (!request || !request.Remarks)
                return false;

            return checkRemarks(request, containsRemarkForSection);
        }

        function organisationHasInvalidRemarksWithoutReason(request) {
            if (!request || !request.Remarks)
                return false;

            return checkRemarks(request, containsIncompleteInvalidRemarks);
        }

        function individualRequestHasInvalidRemarks(request) {
            if (!request || !request.Remarks)
                return false;

            return containsRemarkForSection(request.Remarks, flatten(invalidRemarkCodesByScope.IVM));
        }

        function individualRequestHasInvalidRemarksWithoutReason(request) {
            if (!request || !request.Remarks)
                return false;

            return containsIncompleteInvalidRemarks(request.Remarks, flatten(invalidRemarkCodesByScope.IVM));
        }

        // Implementation - private

        /**
         * Checks all remark objects on an FVM using the validationFunction
         * @param {Object} request 
         * @param {Function} validationFunction 
         * @returns a Boolean indicating whether all remarks are valid according to the passed function.
         */
        function checkRemarks(request, validationFunction) {
            if (!angular.isFunction(validationFunction))
                return false;

            if (validationFunction(request.Remarks, flatten(invalidRemarkCodesByScope.FVM)))
                return true;
            const allJustifications = getAllJustifications(request);
            return checkRemarksOnArray(allJustifications, validationFunction, invalidRemarkCodesByScope.justification)
                || checkRemarksOnArray(request.Persons, validationFunction, invalidRemarkCodesByScope.relations);
        }


        function checkRemarksOnArray(subItems, validationFunction, codes) {
            if (!subItems || subItems.length === 0)
                return false;

            for (const item of subItems) {
                if (validationFunction(item.Remarks, codes))
                    return true;
            }
        }

        /**
         * Checks if an array of remarks contains an indeterminate remark
         * @param {Array} remarks 
         * @returns true if the array contains an indeterminate remark
         */
        function containsIndeterminateRemark(remarks) {
            if (!remarks)
                return false;

            return remarks.some(r => r.indeterminate === true && !r.Deleted);
        }

        /**
         * Checks if any remarks with a section code corresponding to invalid data exists
         * @param {Array} remarks 
         * @param {Array} invalidCodes 
         * @returns true if any of the remarks has an invalid code
         */
        function containsRemarkForSection(remarks, invalidCodes) {
            if (!remarks)
                return false;
            if (!invalidCodes)
                return false;

            return remarks.some(r => !r.Deleted && invalidCodes.includes(r.Section));
        }

        /**
         * Checks whether all remarks invalidating the request have a description
         * @param {Array} remarks 
         * @param {Array} invalidCodes 
         * @returns true if all such remarks have a description/reason
         */
        function containsIncompleteInvalidRemarks(remarks, invalidCodes) {
            if (!remarks)
                return false;
            if (!invalidCodes)
                return false;

            const invalidRemarks = remarks.filter(r => !r.Deleted && invalidCodes.includes(r.Section));

            for (const remark of invalidRemarks) {
                if (!remark.Description)
                    return true;

                if (remark.Description.trim().length === 0)
                    return true;
            }

            return false;
        }

        function getAllJustifications(request) {
            
            const allJustifications = [];

            if (request.Justifications)
                allJustifications.push(...request.Justifications);

            if (request.CopiedJustifications) {
                allJustifications.push(...request.CopiedJustifications.filter(j => j.Action !== null));
            }
            return allJustifications;
        }

        // Helper function

        /**
         * Flattens an object into a single array
         * @param {Object} object 
         */
        function flatten(object) {
            if (!object)
                return [];

            return Object.values(object).flat();
        }
    }
})();
